import React, {useCallback, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import readXlsxFile from 'read-excel-file';
import LinearProgressBar from "../../components/progressbar/linear-progress-bar";
import {Cell} from "read-excel-file/types";
import moment from 'moment';
import {CarType, VehiclePayload} from "../../types/routing-service/vehicle";
import {addLeadingZero, removeLeadingZeros} from "../../utils/date/date-format";
import {toast} from "react-toastify";
import {TaskPayload} from "../../types/routing-service/task";
import {OptimizationType, RoutingPayload, RoutingType} from "../../types/routing-service/routing-request";
import CloseSmallLineIcon from "../../components/icons/CloseSmallLineIcon";
import theme from "../../style/theme";
import styled from "styled-components";
import {styled as muiStyled} from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import FileUploadLineIcon from "../../components/icons/FileUploadLineIcon";
import {OutlinedBlackButton} from "../../components/button/OutlinedBlackButton";
import {ContainedGrayButton} from "../../components/button/ContainedGrayButton";

interface Props {
    handleClickExcelDialogClose: () => void;
}

export const parseVehicleCell = (v: Cell[], date: string, index: number, isAutoEndOptimization: boolean) => {

    const [startHour, startMinute] = v[0].toString().split(":").map(removeLeadingZeros);
    const [endHour, endMinute] = v[1].toString().split(":").map(removeLeadingZeros);

    let car;
    if (v[2] === '스타렉스(150)') {
        car = {
            carType: CarType.Starex,
            maxCapacity: 150,
        }
    } else {
        car = {
            carType: CarType.Ray,
            maxCapacity: 100,
        }
    }

    let endPlace;
    if (isAutoEndOptimization) {
        endPlace = undefined
    } else {
        endPlace = {
            placeName: v[4],
            road: "",
            latitude: 0,
            longitude: 0,
        }
    }

    return {
        vehicleName: `배송원${addLeadingZero(
            `${index + 1}`
        )}`,
        workTimeWindow: {
            startTimeLimited: {
                hour: startHour,
                minute: startMinute,
            },
            endTimeLimited: {
                hour: endHour,
                minute: endMinute,
            },
        },
        car: car,
        startPlace: {
            placeName: v[3],
            road: "",
            latitude: 0,
            longitude: 0,
        },
        endPlace: endPlace,
    } as VehiclePayload
}

export const parseTaskCell = (t: Cell[], date: string, index: number) => {
    const [pickUpStartHour, pickUpStartMinute] = t[1].toString().split(":").map(removeLeadingZeros)
    const [pickUpEndHour, pickUpEndMinute] = t[3].toString().split(":").map(removeLeadingZeros)
    const [dropOffStartHour, dropOffStartMinute] = t[6].toString().split(":").map(removeLeadingZeros)
    const [dropOffEndHour, dropOffEndMinute] = t[8].toString().split(":").map(removeLeadingZeros)

    return {
        taskName: `물류${addLeadingZero(
            `${index + 1}`
        )}`,
        pickUpPlace: {
            placeName: t[0],
            road: "",
            latitude: 0,
            longitude: 0,
        },
        pickUpTimeWindow: {
            startTimeLimited: {
                hour: pickUpStartHour,
                minute: pickUpStartMinute,
            },
            endTimeLimited: {
                hour: pickUpEndHour,
                minute: pickUpEndMinute,
            },
        },
        pickUpServiceTime: t[4] ? t[4].toString() : "0",
        dropOffPlace: {
            placeName: t[5],
            road: "",
            latitude: 0,
            longitude: 0,
        },
        dropOffTimeWindow: {
            startTimeLimited: {
                hour: dropOffStartHour,
                minute: dropOffStartMinute,
            },
            endTimeLimited: {
                hour: dropOffEndHour,
                minute: dropOffEndMinute,
            },
        },
        dropOffServiceTime: t[9] ? Number(t[9]).toString() : "0",
        capacity: t[10] ? Number(t[10]) : 0,
    } as TaskPayload
}

interface Props {
    onUploadPayload: (payload: RoutingPayload) => void;
    handleClickExcelDialogClose: () => void;
}

export const RoutingExcelDialog: React.FC<Props> = (
        {
            onUploadPayload,
            handleClickExcelDialogClose,
        }
    ) => {
        const [file, setFile] = useState<File | null>(null);
        const [progress, setProgress] = useState(0);

        const onDrop = useCallback((acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                setFile(acceptedFiles[0]);
                parseExcel(acceptedFiles[0]);
            }
        }, []);

        const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
            onDrop,
            multiple: false,
            accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
            }
        });

        const parseExcel = async (file: File) => {
            try {
                setProgress(10);
                const rows = await readXlsxFile(file);
                setProgress(50);

                const dateCell: Cell = rows[0][1]; // B1
                let date: Date = new Date();
                let dateString: string = '';
                if (typeof dateCell === 'string' || typeof dateCell === 'number') {
                    date = moment(dateCell.toString()).toDate();
                    dateString = moment(dateCell.toString()).format('YYYY-MM-DD');
                } else if (dateCell instanceof Date) {
                    date = moment(dateCell).toDate();
                    dateString = moment(dateCell).format('YYYY-MM-DD');
                } else {
                    throw new Error("Invalid Date Cell")
                }

                const d1CellValue: Cell = rows[0][3]; // D1
                const routingTypeCellNumber = typeof d1CellValue === 'string' ? parseInt(d1CellValue.split(':')[0]) : 0;
                let routingType = RoutingType.PickupAndDelivery;
                let optimizationType = OptimizationType.TaskEqual;
                switch (routingTypeCellNumber) {
                    case 0:
                        routingType = RoutingType.PickupAndDelivery;
                        optimizationType = OptimizationType.MinDistance;
                        break;
                    case 1:
                        routingType = RoutingType.PickupAndDelivery;
                        optimizationType = OptimizationType.TaskEqual;
                        break;
                    case 2:
                        routingType = RoutingType.PickupAndDelivery;
                        optimizationType = OptimizationType.TimeEqual;
                        break;
                    case 3:
                        routingType = RoutingType.RouteSequential;
                        break;
                }

                const checkboxCell: Cell = rows[0][5]; // F1
                const isAutoEndOptimization = checkboxCell.toString().toUpperCase() === 'Y' && routingType === RoutingType.PickupAndDelivery;

                // A~G
                const vehicles = rows.slice(3)
                    .map(row => row.slice(0, 5))
                    .filter(row => row.some(cell => cell !== null && cell !== undefined))
                    .map((v, index) => {
                        return parseVehicleCell(v, dateString, index, isAutoEndOptimization);
                    });

                const tasks = rows.slice(3)
                    .map(row => row.slice(5, 16))
                    .filter(row => row.some(cell => cell !== null && cell !== undefined))
                    .map((t, index) => {
                        return parseTaskCell(t, dateString, index);
                    });

                // setParsedData({
                //     targetDate: date,
                //     routingType: routingType,
                //     optimizationType: optimizationType,
                //     vehicles: vehicles,
                //     tasks: tasks,
                // });

                onUploadPayload(
                    {
                        targetDate: date,
                        routingType: routingType,
                        optimizationType: optimizationType,
                        vehicles: vehicles,
                        tasks: tasks,
                    }
                )

                setProgress(100);
                toast.success("파일 불러오기에 성공하였습니다.")
            } catch (e) {
                handleDelete();
                toast.error("파일 불러오기에 실패하였습니다. 내용을 다시 확인해주세요.");
            }
        };

        const handleDelete = () => {
            setFile(null);
            // setParsedData(null);
            setProgress(0);
        };

        const downloadExampleFile = () => {
            const excelFileUrl = 'https://ong-test.s3.ap-northeast-2.amazonaws.com/route/%E1%84%85%E1%85%A1%E1%84%8B%E1%85%AE%E1%84%90%E1%85%B5%E1%86%BC+%E1%84%89%E1%85%A5%E1%84%87%E1%85%B5%E1%84%89%E1%85%B3_%E1%84%8B%E1%85%A6%E1%86%A8%E1%84%89%E1%85%A6%E1%86%AF+%E1%84%83%E1%85%B3%E1%86%BC%E1%84%85%E1%85%A9%E1%86%A8_%E1%84%8B%E1%85%A3%E1%86%BC%E1%84%89%E1%85%B5%E1%86%A8.xlsx'
            const fileName = '옹고잉 동선 최적화 엑셀 양식.xlsx'

            fetch(excelFileUrl)
                .then(response => response.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    a.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(error => console.error('Download failed:', error));
        }

        return (
            <BootstrapDialog
                onClose={handleClickExcelDialogClose}
                aria-labelledby="customized-dialog-title"
                open
            >
                <ExcelUploadDialogContainer>
                    <TitleContainer>
                        <TitleLeftContainer>엑셀 등록</TitleLeftContainer>
                        <TitleRightContianer onClick={handleClickExcelDialogClose}>
                            <CloseSmallLineIcon
                                width="1.5rem"
                                height="1.5rem"
                                fill={theme.palette.primitives.gray100}
                            />
                        </TitleRightContianer>
                    </TitleContainer>
                    <BodyContainer
                        {...getRootProps({className: "dropzone"})}>
                        <>
                            <FileUploadLineIconContainer>
                                <FileUploadLineIcon
                                    width="105px"
                                    height="105px"
                                    fill={theme.palette.primitives.gray300}
                                />
                            </FileUploadLineIconContainer>
                            <OutlinedBlackButtonContainer>
                                <OutlinedBlackButton
                                    width="221px"
                                    height="51px"
                                    text="파일 불러오기"
                                    onClick={open}
                                    sx={{
                                        fontWeight: "600",
                                        fontSize: "16px",
                                        fontHeight: "19px",
                                        color: "black",
                                    }}
                                />
                            </OutlinedBlackButtonContainer>
                            <FileFormatInfoLabel>
                                .xlsx 파일만 허용됩니다.
                            </FileFormatInfoLabel>
                            <PlaceHolderContainer>
                                {isDragActive ? "놓으세요!" : "파일을 드래그해서 놓을 수도 있어요!"}
                            </PlaceHolderContainer>
                            <LinearProgressBar progress={progress}/>
                        </>
                    </BodyContainer>
                    <FooterContainer>
                        <CreateButtonContainer>
                            <ContainedGrayButton
                                width="258px"
                                height="51px"
                                text="엑셀 양식 파일 다운로드"
                                onClick={downloadExampleFile}
                            />
                        </CreateButtonContainer>
                    </FooterContainer>
                </ExcelUploadDialogContainer>
            </BootstrapDialog>
        );
    }
;


const ExcelUploadDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 436px;
  border-radius: 0.5rem;
`;

const TitleContainer = styled.div`
  height: 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${theme.palette.primitives.black};
`;

const TitleLeftContainer = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray50};
  margin-left: 1.5rem;
`;

const TitleRightContianer = styled.div`
  margin-right: 0.438rem;
  cursor: pointer;
`;

const BodyContainer = styled.div`
  overflow-y: auto;
  padding: 20px 24px;
  align-items: center;
  justify-items: center;
  text-align: center;
  margin: 0 auto;
  justify-content: center;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 24px 20px;
`;

const CreateButtonContainer = styled.div`
  margin-left: 16px;
`;

const BootstrapDialog = muiStyled(Dialog)(({theme}) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

const FileUploadLineIconContainer = styled.div`
  margin-top: 30px;
`;

const OutlinedBlackButtonContainer = styled.div`
  margin-top: 24px;
`;

const PlaceHolderContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 60px;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: ${theme.palette.primitives.gray500};
`;

const FileFormatInfoLabel = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  color: ${theme.palette.primitives.gray400};
`;


export default RoutingExcelDialog;
